// CS01 deadline calculator.
//
// The live company-number lookup path has been removed in this phase, so the
// widget now runs in manual mode only.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, AlertTriangle, CheckCircle2, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { trackEvent, GA_EVENTS } from "@/lib/analytics";

function addYears(iso, years) {
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

function daysBetween(a, b) {
  const ms = b.getTime() - a.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function fmt(d) {
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function CS01Calculator() {
  const [manualDate, setManualDate] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  function computeFromDate(reviewPeriodStartISO, companyName) {
    // Companies House rule: review period is rolling 12 months from last CS01
    // (or from incorporation, for first CS01). Filing deadline is 14 days
    // after the review period ends.
    const reviewStart = new Date(reviewPeriodStartISO);
    const reviewEnd = addYears(reviewPeriodStartISO, 1);
    const filingDeadline = new Date(reviewEnd);
    filingDeadline.setDate(filingDeadline.getDate() + 14);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysToDeadline = daysBetween(today, filingDeadline);
    const status = daysToDeadline < 0 ? "overdue" : daysToDeadline <= 14 ? "urgent" : daysToDeadline <= 30 ? "approaching" : "ok";
    setResult({
      companyName: companyName || null,
      reviewStart,
      reviewEnd,
      filingDeadline,
      daysToDeadline,
      status,
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!manualDate) {
      setError("Pick the review period start (date of incorporation, or the day after your last confirmation statement).");
      return;
    }
    computeFromDate(manualDate, null);
    trackEvent(GA_EVENTS.CS01_CALCULATOR_USED || "cs01_calc_used", { mode: "manual" });
  }

  const statusStyles = {
    overdue: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-800", icon: AlertTriangle, label: "OVERDUE" },
    urgent: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", icon: AlertTriangle, label: "URGENT" },
    approaching: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-900", icon: Calendar, label: "APPROACHING" },
    ok: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: CheckCircle2, label: "PLENTY OF TIME" },
  };

  return (
    <section
      data-testid="cs01-calculator"
      className="my-12 rounded-3xl border border-border bg-secondary/30 p-6 md:p-8"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-accent/15 text-accent inline-flex items-center justify-center shrink-0">
          <Calendar className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display text-xl font-bold text-primary tracking-tight">CS01 deadline calculator</h3>
          <p className="text-sm text-muted-foreground">
            Pick the review period start and we will work out the next confirmation statement due date.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-5 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="date"
            data-testid="cs01-calc-manual-date"
            value={manualDate}
            onChange={(e) => setManualDate(e.target.value)}
            className="h-11"
            aria-label="Review period start date"
          />
          <Button type="submit" data-testid="cs01-calc-manual-submit" className="h-11">Calculate</Button>
        </div>
        <div className="text-xs text-muted-foreground">
          Live company-number lookup is unavailable in this phase.
        </div>
      </form>

      {error && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-start gap-2" data-testid="cs01-calc-error">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (() => {
        const s = statusStyles[result.status];
        const Icn = s.icon;
        return (
          <div
            className={`mt-5 rounded-2xl border ${s.border} ${s.bg} p-5`}
            data-testid="cs01-calc-result"
          >
            <div className={`inline-flex items-center gap-2 text-xs font-bold tracking-wider ${s.text}`}>
              <Icn className="h-4 w-4" /> {s.label}
            </div>
            {result.companyName && (
              <div className="mt-2 font-display text-lg font-bold text-primary">{result.companyName}</div>
            )}
            <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">Review period ends</div>
                <div className="mt-1 font-display text-base font-bold text-primary">{fmt(result.reviewEnd)}</div>
              </div>
              <div>
                <div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">Filing deadline</div>
                <div className={`mt-1 font-display text-base font-bold ${s.text}`}>{fmt(result.filingDeadline)}</div>
              </div>
              <div>
                <div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">Days remaining</div>
                <div className={`mt-1 font-display text-base font-bold ${s.text}`}>
                  {result.daysToDeadline < 0 ? `${Math.abs(result.daysToDeadline)} days overdue` : `${result.daysToDeadline} days`}
                </div>
              </div>
            </div>
            {(result.status === "urgent" || result.status === "overdue") && (
              <div className="mt-4 rounded-xl bg-white p-4 border border-border flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="text-sm text-primary">
                  Your CS01 is {result.status === "overdue" ? "overdue" : "due in 14 days or less"}. CoSec App can file it for you today.
                </div>
                <Link
                  to="/contact"
                  data-testid="cs01-calc-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-accent text-primary font-bold px-4 py-2 text-sm hover:bg-accent/90 transition-colors"
                >
                  <Phone className="h-4 w-4" /> Book a demo
                </Link>
              </div>
            )}
            <div className="mt-3 text-[11px] text-muted-foreground">
              Source: Companies House public register. CoSec Global is not affiliated with Companies House. Always verify the deadline on the Companies House website before filing.
            </div>
          </div>
        );
      })()}
    </section>
  );
}
